'use client';

import { Employee, EmployeeFilters, EmployeeStats } from '@/types/employees';
import { supabase } from '@/lib/supabase';

// Historical alias — this used to be a separate client pointing at the same
// Supabase project, which caused "Multiple GoTrueClient instances" warnings
// and session races. Now it's just the main client under its legacy name.
export const orgDataClient = supabase;

type SortDirection = 'asc' | 'desc';
type SortableColumn = 'full_name' | 'designation' | 'department' | 'company' | 'employee_status' | 'date_of_joining';

interface GetEmployeesParams {
  page?: number;
  perPage?: number;
  sortBy?: SortableColumn;
  sortDirection?: SortDirection;
  filters?: EmployeeFilters;
  groupBy?: string;
}

/**
 * Fetches employees with filtering, sorting, and pagination
 */
export async function getEmployees({
  page = 1,
  perPage = 10,
  sortBy = 'full_name',
  sortDirection = 'asc',
  filters = {},
  groupBy
}: GetEmployeesParams) {
  try {
    console.log('getEmployees called with filters:', filters);
    let query = orgDataClient
      .from('active_employees')
      .select('*', { count: 'exact' });

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        // Handle "Unassigned" department filter
        if (key === 'department' && value === 'Unassigned') {
          console.log('🔍 Filtering for Unassigned department (NULL values)');
          query = query.is('department', null);
        } else {
          console.log(`🔍 Filtering ${key} = ${value}`);
          query = query.eq(key, value);
        }
      }
    });

    // Apply sorting
    if (sortBy && sortDirection) {
      query = query.order(sortBy, { ascending: sortDirection === 'asc' });
    }

    // If grouping, apply a secondary sort by the group field
    if (groupBy && groupBy !== '' && groupBy !== sortBy) {
      query = query.order(groupBy, { ascending: true });
    }

    // Apply pagination
    const from = (page - 1) * perPage;
    query = query.range(from, from + perPage - 1);

    console.log('Executing query...');
    const { data, error, count } = await query;

    console.log('Query result:', { dataCount: data?.length, totalCount: count, error });

    if (error) throw error;
    
    return { 
      data: data as Employee[], 
      count: count || 0,
      error: null 
    };
  } catch (error) {
    console.error('Error fetching employees:', error);
    return { 
      data: [], 
      count: 0, 
      error 
    };
  }
}

/**
 * Gets employee statistics
 */
export async function getEmployeeStats(): Promise<EmployeeStats> {
  try {
    // Get total active employees count (view already excludes archived + past LWD)
    const { count: totalEmployees } = await orgDataClient
      .from('active_employees')
      .select('*', { count: 'exact', head: true });

    // Active count is same as total since view handles filtering
    const activeCount = totalEmployees;

    // Get new hires in last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];

    const { count: newHires } = await orgDataClient
      .from('active_employees')
      .select('*', { count: 'exact', head: true })
      .gte('date_of_joining', thirtyDaysAgoStr);
    
    return {
      total: totalEmployees || 0, // Total employee count
      activeCount: activeCount || 0, // Active employee count
      newHires: newHires || 0,
      activePercentage: totalEmployees && activeCount ? Math.round((activeCount / totalEmployees) * 100) : 0
    };
  } catch (error) {
    console.error('Error fetching employee stats:', error);
    return {
      total: 0,
      activeCount: 0,
      newHires: 0,
      activePercentage: 0
    };
  }
}

/**
 * Gets unique values for filtering
 */
export async function getFilterOptions() {
  try {
    // Use RPC calls to get distinct values efficiently
    const { data: departments } = await orgDataClient
      .rpc('get_distinct_departments');

    const { data: companies } = await orgDataClient
      .rpc('get_distinct_companies');

    const { data: employmentTypes } = await orgDataClient
      .rpc('get_distinct_employment_types');

    // Convert to option format - RPC functions return rows with column names
    const departmentOptions = (departments || [])
      .map((row: { department: string }) => row.department)
      .filter(Boolean)
      .map((name: string) => ({ id: name, name }));

    const companyOptions = (companies || [])
      .map((row: { company: string }) => row.company)
      .filter(Boolean)
      .map((name: string) => ({ id: name, name }));

    const employmentTypeOptions = (employmentTypes || [])
      .map((row: { employment_type: string }) => row.employment_type)
      .filter(Boolean)
      .map((name: string) => ({ id: name, name }));

    return {
      departments: departmentOptions,
      companies: companyOptions,
      employmentTypes: employmentTypeOptions
    };
  } catch (error) {
    console.error('Error fetching filter options:', error);
    return {
      departments: [],
      companies: [],
      employmentTypes: []
    };
  }
}

/**
 * Gets employee counts grouped by department
 */
export async function getDepartmentBreakdown() {
  try {
    const { data, error } = await orgDataClient.rpc('get_department_counts');
    
    if (error) throw error;
    
    return {
      data: data || [],
      error: null
    };
  } catch (error) {
    console.error('Error fetching department breakdown:', error);
    return {
      data: [],
      error
    };
  }
}

/**
 * Gets employee counts grouped by company
 */
export async function getCompanyBreakdown() {
  try {
    const { data, error } = await orgDataClient.rpc('get_company_counts');
    
    if (error) throw error;
    
    return {
      data: data || [],
      error: null
    };
  } catch (error) {
    console.error('Error fetching company breakdown:', error);
    return {
      data: [],
      error
    };
  }
}
